// @ts-nocheck — vendored ManualAnnotation camera-math; typechecked upstream, exempt from host strict flags (noUncheckedIndexedAccess/exactOptionalPropertyTypes).
// useEpipolarCurve — compute the epipolar constraint curve on `side`.
//
// Anchor relationships (which view provides the reference point):
//   front_right ← front_left      (front pair: fixed)
//   front_left  ← front_right     (front pair: fixed)
//   left        ← front_left OR front_right   (per-keypoint selection)
//   right       ← front_left OR front_right   (per-keypoint selection)
//
// Per-keypoint qualified-aware gating for the front pair (uses pred.qualified):
//   front_left  is constrained iff FL is unqualified AND FR is qualified.
//   front_right is constrained iff FR is unqualified (FL is the default driver
//               when both are unqualified).
//
// Side-view anchor selection (per keypoint):
//   - Both FL and FR qualified: use whichever has the larger pred.score.
//   - Exactly one qualified: use that view.
//   - Neither qualified: use FL as the default anchor.
//   - Graceful fallback: if the chosen anchor has no position (pred or edit),
//     fall back to the other front view; if neither has a position, no curve.
//   Cross-side transforms (FR→L, FL→R) are composed on the fly from the
//   already-loaded same-side transforms + the FL↔FR front-pair transform.
//
// Supports both KB4 and Scaramuzza camera models.
// Returns curve=null when calibration is absent or incomplete for the side, or
// when the qualified-aware gate disables the constraint.

import * as React from 'react';
import {
  computeEpipolarCurve,
  computeEpipolarCurveDs,
  computeEpipolarCurveScaramuzza,
  invertStereo,
} from '../lib/epipolar';
import { useStore } from '../store';
import type { Side } from '../store/predictions';
import type { KeypointName } from '../types';

type Mat3 = number[][];
type Vec3 = [number, number, number];

export interface EpipolarResult {
  curve: [number, number][] | null;
  anchorName: KeypointName | null;
}

// Compose (R1, T1): A→B with (R2, T2): B→C into (R, T): A→C.
// P_C = R2·(R1·P_A + T1) + T2 = (R2·R1)·P_A + (R2·T1 + T2)
function composeStereo(R1: Mat3, T1: Vec3, R2: Mat3, T2: Vec3): { R: Mat3; T: Vec3 } {
  const R: Mat3 = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) R[i][j] += R2[i][k] * R1[k][j];
  const T: Vec3 = [
    R2[0][0] * T1[0] + R2[0][1] * T1[1] + R2[0][2] * T1[2] + T2[0],
    R2[1][0] * T1[0] + R2[1][1] * T1[1] + R2[1][2] * T1[2] + T2[1],
    R2[2][0] * T1[0] + R2[2][1] * T1[1] + R2[2][2] * T1[2] + T2[2],
  ];
  return { R, T };
}

export function useEpipolarCurve(side: Side): EpipolarResult {
  const calibration = useStore((s) => s.calibration);
  const currentFrame = useStore((s) => s.currentFrame);

  const focusName = useStore((s) => {
    if (side === 'front_left') return s.selectedFrontLeft;
    if (side === 'front_right') return s.selectedFrontRight;
    if (side === 'left') return s.selectedLeft;
    return s.selectedRight;
  });

  // Both front views' preds/edits — used by side views for anchor selection,
  // and by front views for the existing qualified-aware gate.
  const predFL = useStore((s) => s.predFrontLeft);
  const predFR = useStore((s) => s.predFrontRight);
  const editsFL = useStore((s) => s.editsFrontLeft);
  const editsFR = useStore((s) => s.editsFrontRight);

  const selfPreds = useStore((s) => {
    if (side === 'front_left') return s.predFrontLeft;
    if (side === 'front_right') return s.predFrontRight;
    if (side === 'left') return s.predLeft;
    return s.predRight;
  });

  return React.useMemo((): EpipolarResult => {
    if (!calibration || !focusName) {
      return { curve: null, anchorName: null };
    }

    const { R, T, flSize, frSize } = calibration;

    // ---- Side views: dual-anchor selection ---------------------------------
    if (side === 'left' || side === 'right') {
      const flPred = predFL?.[currentFrame]?.find((r) => r.name === focusName);
      const frPred = predFR?.[currentFrame]?.find((r) => r.name === focusName);
      const flQ = !!flPred?.qualified;
      const frQ = !!frPred?.qualified;

      let useFL: boolean;
      if (flQ && frQ) useFL = flPred.score >= frPred.score;
      else if (flQ) useFL = true;
      else if (frQ) useFL = false;
      else useFL = true; // rule (3): default to front_left

      const flEdit = editsFL[currentFrame]?.[focusName];
      const frEdit = editsFR[currentFrame]?.[focusName];
      const flXY = flEdit?.xy ?? flPred?.xy ?? null;
      const frXY = frEdit?.xy ?? frPred?.xy ?? null;
      const flUsable = !!flXY && flEdit?.visibility !== 'out_of_frame';
      const frUsable = !!frXY && frEdit?.visibility !== 'out_of_frame';

      if (useFL && !flUsable && frUsable) useFL = false;
      else if (!useFL && !frUsable && flUsable) useFL = true;
      if (!flUsable && !frUsable) {
        return { curve: null, anchorName: focusName };
      }

      const anchorPx = (useFL ? flXY : frXY) as [number, number];

      let curve: [number, number][] | null = null;

      if (calibration.cameraModel === 'scaramuzza') {
        const { flTaylor, frTaylor, flCenter, frCenter } = calibration;
        if (!flTaylor || !frTaylor || !flCenter || !frCenter) {
          return { curve: null, anchorName: focusName };
        }

        if (side === 'left') {
          if (
            !calibration.lTaylor ||
            !calibration.lCenter ||
            !calibration.R_fl_l ||
            !calibration.T_fl_l ||
            !calibration.lSize
          ) {
            return { curve: null, anchorName: focusName };
          }
          const dstC = calibration.lCenter;
          const dstP = calibration.lTaylor;
          const dstSz = calibration.lSize;
          let R_sd: Mat3;
          let T_sd: Vec3;
          let srcCx: number;
          let srcCy: number;
          let srcP: number[];
          if (useFL) {
            R_sd = calibration.R_fl_l;
            T_sd = calibration.T_fl_l;
            srcCx = flCenter[0];
            srcCy = flCenter[1];
            srcP = flTaylor;
          } else {
            // FR → L = (FR → FL) composed with (FL → L)
            const { R_inv, T_inv } = invertStereo(R, T);
            const c = composeStereo(R_inv, T_inv, calibration.R_fl_l, calibration.T_fl_l);
            R_sd = c.R;
            T_sd = c.T;
            srcCx = frCenter[0];
            srcCy = frCenter[1];
            srcP = frTaylor;
          }
          curve = computeEpipolarCurveScaramuzza(
            anchorPx,
            srcCx,
            srcCy,
            srcP,
            dstC[0],
            dstC[1],
            dstP,
            R_sd,
            T_sd,
            dstSz
          );
        } else {
          // side === 'right'
          if (
            !calibration.rTaylor ||
            !calibration.rCenter ||
            !calibration.R_fr_r ||
            !calibration.T_fr_r ||
            !calibration.rSize
          ) {
            return { curve: null, anchorName: focusName };
          }
          const dstC = calibration.rCenter;
          const dstP = calibration.rTaylor;
          const dstSz = calibration.rSize;
          let R_sd: Mat3;
          let T_sd: Vec3;
          let srcCx: number;
          let srcCy: number;
          let srcP: number[];
          if (!useFL) {
            R_sd = calibration.R_fr_r;
            T_sd = calibration.T_fr_r;
            srcCx = frCenter[0];
            srcCy = frCenter[1];
            srcP = frTaylor;
          } else {
            // FL → R = (FL → FR) composed with (FR → R)
            const c = composeStereo(R, T, calibration.R_fr_r, calibration.T_fr_r);
            R_sd = c.R;
            T_sd = c.T;
            srcCx = flCenter[0];
            srcCy = flCenter[1];
            srcP = flTaylor;
          }
          curve = computeEpipolarCurveScaramuzza(
            anchorPx,
            srcCx,
            srcCy,
            srcP,
            dstC[0],
            dstC[1],
            dstP,
            R_sd,
            T_sd,
            dstSz
          );
        }
      } else if (calibration.cameraModel === 'ds') {
        const { flDs, frDs } = calibration;
        if (!flDs || !frDs) {
          return { curve: null, anchorName: focusName };
        }
        if (side === 'left') {
          if (
            !calibration.lDs ||
            !calibration.R_fl_l ||
            !calibration.T_fl_l ||
            !calibration.lSize
          ) {
            return { curve: null, anchorName: focusName };
          }
          let R_sd: Mat3;
          let T_sd: Vec3;
          let srcDs = flDs;
          if (useFL) {
            R_sd = calibration.R_fl_l;
            T_sd = calibration.T_fl_l;
          } else {
            const { R_inv, T_inv } = invertStereo(R, T);
            const c = composeStereo(R_inv, T_inv, calibration.R_fl_l, calibration.T_fl_l);
            R_sd = c.R;
            T_sd = c.T;
            srcDs = frDs;
          }
          curve = computeEpipolarCurveDs(
            anchorPx,
            srcDs,
            calibration.lDs,
            R_sd,
            T_sd,
            calibration.lSize
          );
        } else {
          if (
            !calibration.rDs ||
            !calibration.R_fr_r ||
            !calibration.T_fr_r ||
            !calibration.rSize
          ) {
            return { curve: null, anchorName: focusName };
          }
          let R_sd: Mat3;
          let T_sd: Vec3;
          let srcDs = frDs;
          if (!useFL) {
            R_sd = calibration.R_fr_r;
            T_sd = calibration.T_fr_r;
          } else {
            const c = composeStereo(R, T, calibration.R_fr_r, calibration.T_fr_r);
            R_sd = c.R;
            T_sd = c.T;
            srcDs = flDs;
          }
          curve = computeEpipolarCurveDs(
            anchorPx,
            srcDs,
            calibration.rDs,
            R_sd,
            T_sd,
            calibration.rSize
          );
        }
      } else {
        // KB4 model
        const { flK, frK, flD, frD } = calibration;
        if (!flK || !frK || !flD || !frD) {
          return { curve: null, anchorName: focusName };
        }
        if (side === 'left') {
          if (
            !calibration.lK ||
            !calibration.lD ||
            !calibration.R_fl_l ||
            !calibration.T_fl_l ||
            !calibration.lSize
          ) {
            return { curve: null, anchorName: focusName };
          }
          let R_sd: Mat3;
          let T_sd: Vec3;
          let srcK: Mat3;
          let srcD: [number, number, number, number];
          if (useFL) {
            R_sd = calibration.R_fl_l;
            T_sd = calibration.T_fl_l;
            srcK = flK;
            srcD = flD;
          } else {
            const { R_inv, T_inv } = invertStereo(R, T);
            const c = composeStereo(R_inv, T_inv, calibration.R_fl_l, calibration.T_fl_l);
            R_sd = c.R;
            T_sd = c.T;
            srcK = frK;
            srcD = frD;
          }
          curve = computeEpipolarCurve(
            anchorPx,
            srcK,
            srcD,
            calibration.lK,
            calibration.lD,
            R_sd,
            T_sd,
            calibration.lSize
          );
        } else {
          if (
            !calibration.rK ||
            !calibration.rD ||
            !calibration.R_fr_r ||
            !calibration.T_fr_r ||
            !calibration.rSize
          ) {
            return { curve: null, anchorName: focusName };
          }
          let R_sd: Mat3;
          let T_sd: Vec3;
          let srcK: Mat3;
          let srcD: [number, number, number, number];
          if (!useFL) {
            R_sd = calibration.R_fr_r;
            T_sd = calibration.T_fr_r;
            srcK = frK;
            srcD = frD;
          } else {
            const c = composeStereo(R, T, calibration.R_fr_r, calibration.T_fr_r);
            R_sd = c.R;
            T_sd = c.T;
            srcK = flK;
            srcD = flD;
          }
          curve = computeEpipolarCurve(
            anchorPx,
            srcK,
            srcD,
            calibration.rK,
            calibration.rD,
            R_sd,
            T_sd,
            calibration.rSize
          );
        }
      }

      return { curve, anchorName: focusName };
    }

    // ---- Front pair: existing qualified-aware logic ------------------------
    const anchorPreds = side === 'front_left' ? predFR : predFL;
    const anchorEdits = side === 'front_left' ? editsFR : editsFL;

    const edit = anchorEdits[currentFrame]?.[focusName];
    const pred = anchorPreds?.[currentFrame]?.find((r) => r.name === focusName);
    const anchorPx = edit?.xy ?? pred?.xy ?? null;

    if (!anchorPx) return { curve: null, anchorName: focusName };
    if (edit?.visibility === 'out_of_frame') {
      return { curve: null, anchorName: focusName };
    }

    const selfPred = selfPreds?.[currentFrame]?.find((r) => r.name === focusName);
    const selfQualified = !!selfPred?.qualified;
    const anchorQualified = !!pred?.qualified;
    if (side === 'front_left') {
      if (selfQualified || !anchorQualified) {
        return { curve: null, anchorName: focusName };
      }
    } else if (side === 'front_right') {
      if (selfQualified) {
        return { curve: null, anchorName: focusName };
      }
    }

    let curve: [number, number][] | null = null;

    if (calibration.cameraModel === 'scaramuzza') {
      const { flTaylor, frTaylor, flCenter, frCenter } = calibration;
      if (!flTaylor || !frTaylor || !flCenter || !frCenter) {
        return { curve: null, anchorName: focusName };
      }
      if (side === 'front_right') {
        curve = computeEpipolarCurveScaramuzza(
          anchorPx,
          flCenter[0],
          flCenter[1],
          flTaylor,
          frCenter[0],
          frCenter[1],
          frTaylor,
          R,
          T,
          frSize
        );
      } else {
        // side === 'front_left'
        const { R_inv, T_inv } = invertStereo(R, T);
        curve = computeEpipolarCurveScaramuzza(
          anchorPx,
          frCenter[0],
          frCenter[1],
          frTaylor,
          flCenter[0],
          flCenter[1],
          flTaylor,
          R_inv,
          T_inv,
          flSize
        );
      }
    } else if (calibration.cameraModel === 'ds') {
      const { flDs, frDs } = calibration;
      if (!flDs || !frDs) {
        return { curve: null, anchorName: focusName };
      }
      if (side === 'front_right') {
        curve = computeEpipolarCurveDs(anchorPx, flDs, frDs, R, T, frSize);
      } else {
        const { R_inv, T_inv } = invertStereo(R, T);
        curve = computeEpipolarCurveDs(anchorPx, frDs, flDs, R_inv, T_inv, flSize);
      }
    } else {
      const { flK, frK, flD, frD } = calibration;
      if (!flK || !frK || !flD || !frD) {
        return { curve: null, anchorName: focusName };
      }
      if (side === 'front_right') {
        curve = computeEpipolarCurve(anchorPx, flK, flD, frK, frD, R, T, frSize);
      } else {
        const { R_inv, T_inv } = invertStereo(R, T);
        curve = computeEpipolarCurve(anchorPx, frK, frD, flK, flD, R_inv, T_inv, flSize);
      }
    }

    return { curve, anchorName: focusName };
  }, [calibration, focusName, currentFrame, side, predFL, predFR, editsFL, editsFR, selfPreds]);
}
